import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { QuietOffersCard } from "@/components/GrowthCards";
import { BuilderTour, BuilderTourButton, keepDialogOpenForTour } from "@/components/PageTour";
import { CAMPAIGNS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichEditor from "@/components/RichEditor";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Send, Users2, MailCheck, MailX, Monitor, Smartphone, Eye, MousePointerClick } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { toast } from "sonner";

const DEFAULT_HTML = `<div style="font-family:Georgia,serif;color:#1e293b;line-height:1.6">
  <h1 style="font-family:Georgia,serif;color:#0a1128;font-weight:400;font-size:28px;margin-bottom:4px">Thank you for staying with Newport Stays</h1>
  <p>Dear guest,</p>
  <p>We hope your recent stay was as restful as the estuary at dawn. We'd love to welcome you back — as a returning guest you'll always receive our best available rate.</p>
  <p style="margin-top:24px">
    <a href="https://newportstays.co.uk" style="background:#0d7a75;color:white;text-decoration:none;padding:12px 22px;border-radius:999px">Plan your next stay</a>
  </p>
  <p style="margin-top:32px;color:#64748b;font-size:12px">Warmest regards, Newport Stays</p>
</div>`;

export default function Campaigns() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [form, setForm] = useState({
    name: "",
    subject: "",
    html_content: DEFAULT_HTML,
    segment_type: "guest",
    reply_to: "",
    template_id: "",
    scheduled_for: "",
  });

  const load = () => {
    api.get("/campaigns").then((r) => setRows(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (user?.email) {
      const preferred = user.reply_to_email || user.email;
      setForm((f) => (f.reply_to ? f : { ...f, reply_to: preferred }));
    }
  }, [user?.email, user?.reply_to_email]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t)
      setForm((f) => ({
        ...f,
        template_id: id,
        subject: t.subject,
        html_content: t.html_content,
      }));
  };

  const runPreview = async () => {
    try {
      const { data } = await api.post("/campaigns/preview", {
        ...form,
        segment_type: form.segment_type || null,
        reply_to: form.reply_to || null,
      });
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const send = async () => {
    if (!form.name || !form.subject) {
      toast.error("Give your campaign a name and subject");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        segment_type: form.segment_type || null,
        reply_to: form.reply_to || null,
      };
      if (form.scheduled_for) {
        payload.scheduled_for = new Date(form.scheduled_for).toISOString();
      } else {
        payload.scheduled_for = null;
      }
      const { data } = await api.post("/campaigns", payload);
      if (data.status === "scheduled") {
        toast.success(
          `Scheduled — will send to ${data.total_recipients} contact(s) at ${new Date(data.scheduled_for).toLocaleString()}`,
        );
      } else {
        toast.success(
          `Sent to ${data.sent_count} recipient(s) · ${data.failed_count} failed`,
        );
      }
      setOpen(false);
      setPreview(null);
      setForm({
        name: "",
        subject: "",
        html_content: DEFAULT_HTML,
        segment_type: "guest",
        reply_to: user?.reply_to_email || user?.email || "",
        template_id: "",
        scheduled_for: "",
      });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("Cancel this scheduled campaign?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Scheduled campaign cancelled");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const sendTest = async () => {
    if (!form.subject.trim()) {
      toast.error("Add a subject first");
      return;
    }
    if (!testEmail) {
      toast.error("Enter an email to send the test to");
      return;
    }
    setSendingTest(true);
    try {
      await api.post("/campaigns/test", {
        subject: form.subject,
        html_content: form.html_content,
        reply_to: form.reply_to || null,
        test_email: testEmail,
      });
      toast.success(`Test sent to ${testEmail}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Layout
      title="Campaigns"
      subtitle="Post-departure mail-shots and updates for every guest, contractor and partner in your CRM."
      actions={
        <Button
          data-testid={CAMPAIGNS.newBtn}
          onClick={() => setOpen(true)}
          className="rounded-full ns-btn-primary h-11 px-6"
        >
          <Plus className="w-4 h-4 mr-2" /> New campaign
        </Button>
      }
    >
      <div data-testid={CAMPAIGNS.root} className="grid gap-4">
        <QuietOffersCard />
        {rows.length === 0 && (
          <div className="ns-card p-12 text-center">
            <Send className="w-8 h-8 text-teal-600 mx-auto" />
            <div className="font-serif text-2xl mt-4">No campaigns yet</div>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Create your first mail-shot to reach past guests with a
              thank-you note or a seasonal invitation.
            </p>
          </div>
        )}
        {rows.map((c) => (
          <button
            key={c.id}
            onClick={() => {
              setDetail(c);
              setAnalytics(null);
              if (c.status !== "scheduled") {
                api
                  .get(`/campaigns/${c.id}/analytics`)
                  .then((r) => setAnalytics(r.data))
                  .catch(() => setAnalytics(null));
              }
            }}
            className="ns-card p-6 text-left hover:-translate-y-0.5"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="ns-label flex items-center gap-2">
                  {c.segment_type || "everyone"}
                  {c.status === "scheduled" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-50 text-amber-800 border border-amber-100">
                      Scheduled · {new Date(c.scheduled_for).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="font-serif text-2xl mt-1">{c.name}</div>
                <div className="text-sm text-slate-500 mt-1">{c.subject}</div>
              </div>
              <div className="text-right">
                {c.status === "scheduled" ? (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`campaigns-cancel-${c.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelScheduled(c.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        cancelScheduled(c.id);
                      }
                    }}
                    className="text-xs text-slate-500 hover:text-red-600 hover:underline cursor-pointer"
                  >
                    Cancel
                  </span>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-teal-700 text-sm">
                      <MailCheck className="w-4 h-4" /> {c.sent_count}
                    </span>
                    <span className="inline-flex items-center gap-1 text-red-500 text-sm">
                      <MailX className="w-4 h-4" /> {c.failed_count}
                    </span>
                  </div>
                )}
                <div className="text-[11px] text-slate-400 mt-1">
                  {new Date(c.created_at).toLocaleString()}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* New campaign dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" onInteractOutside={keepDialogOpenForTour}>
          <BuilderTour open={open} />
          <DialogHeader className="flex-row items-center justify-between gap-3 pr-8">
            <DialogTitle className="font-serif text-2xl">
              New mail-shot
            </DialogTitle>
            <BuilderTourButton />
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2">
              <Label className="ns-label">Campaign name (internal)</Label>
              <Input
                data-testid={CAMPAIGNS.nameInput}
                className="mt-2 h-11 bg-white"
                placeholder="Autumn thank-you 2026"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div>
              <Label className="ns-label">Send to</Label>
              <Select
                value={form.segment_type || "all"}
                onValueChange={(v) =>
                  setForm({ ...form, segment_type: v === "all" ? "" : v })
                }
              >
                <SelectTrigger
                  data-testid={CAMPAIGNS.segmentType}
                  className="mt-2 h-11 bg-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone in CRM</SelectItem>
                  <SelectItem value="guest">Past guests</SelectItem>
                  <SelectItem value="contractor">Contractors</SelectItem>
                  <SelectItem value="supplier">Suppliers</SelectItem>
                  <SelectItem value="owner">Owners / Partners</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="ns-label">Start from template</Label>
              <Select value={form.template_id} onValueChange={applyTemplate}>
                <SelectTrigger
                  data-testid={CAMPAIGNS.templateSelect}
                  className="mt-2 h-11 bg-white"
                >
                  <SelectValue placeholder="None — write from scratch" />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">
                      No templates saved yet.
                    </div>
                  )}
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Subject line</Label>
              <Input
                data-testid={CAMPAIGNS.subjectInput}
                className="mt-2 h-11 bg-white"
                placeholder="Thank you for staying with Newport Stays"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Reply-to email</Label>
              <Input
                type="email"
                className="mt-2 h-11 bg-white"
                placeholder="hello@newportstays.co.uk"
                value={form.reply_to}
                onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
              />
              <div className="text-[11px] text-slate-400 mt-1.5">
                Replies from recipients will land in this inbox. Prefilled with
                your account email — edit if you&apos;d rather use a shared one.
              </div>
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Schedule (optional)</Label>
              <Input
                data-testid="campaigns-schedule-input"
                type="datetime-local"
                className="mt-2 h-11 bg-white"
                value={form.scheduled_for}
                onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
              />
              <div className="text-[11px] text-slate-400 mt-1.5">
                Leave blank to send immediately. Set a future date/time to save it as a
                draft — the dispatcher fires every 15 minutes.
              </div>
            </div>

            <div className="col-span-2">
              <Label className="ns-label">Body</Label>
              <div className="mt-2">
                <RichEditor
                  testId={CAMPAIGNS.htmlInput}
                  value={form.html_content}
                  onChange={(html) => setForm({ ...form, html_content: html })}
                  placeholder="Compose your mail-shot to past guests…"
                  minHeight={320}
                />
              </div>
              <div className="text-[11px] text-slate-400 mt-1.5">
                Merge tags: <code>{"{{first_name}}"}</code>{" "}
                <code>{"{{last_name}}"}</code> <code>{"{{property}}"}</code>{" "}
                <code>{"{{check_in}}"}</code> <code>{"{{check_out}}"}</code> — filled per recipient at send time.
              </div>
            </div>

            <div className="col-span-2">
              <div className="flex items-center justify-between mb-3">
                <Label className="ns-label">Inbox preview</Label>
                <div className="flex bg-stone-100 rounded-full p-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setPreviewDevice("desktop")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
                      previewDevice === "desktop"
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" /> Desktop
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewDevice("mobile")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
                      previewDevice === "mobile"
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" /> Mobile
                  </button>
                </div>
              </div>

              <div className="bg-stone-100 rounded-2xl p-4 md:p-6 flex justify-center">
                <div
                  className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(10,17,40,0.08)] overflow-hidden w-full"
                  style={{
                    maxWidth: previewDevice === "mobile" ? 380 : 640,
                    transition: "max-width 220ms ease",
                  }}
                >
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-600 text-white font-serif flex items-center justify-center">
                      N
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        Newport Stays
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        to: guest@example.com
                        {form.reply_to ? ` · reply-to ${form.reply_to}` : ""}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 tracking-wider uppercase">
                      Just now
                    </div>
                  </div>

                  <div className="px-5 pt-4 pb-2">
                    <div className="font-serif text-xl text-slate-900 leading-tight">
                      {form.subject || (
                        <span className="text-slate-300">Subject line…</span>
                      )}
                    </div>
                  </div>
                  <div className="ns-hairline mx-5" />

                  <div
                    className="px-5 py-5"
                    style={{
                      fontFamily: "Georgia, serif",
                      color: "#1e293b",
                      lineHeight: 1.6,
                      fontSize: previewDevice === "mobile" ? "14px" : "15px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html:
                        form.html_content ||
                        '<p style="color:#94a3b8">Your email body will appear here as you compose it above.</p>',
                    }}
                  />
                </div>
              </div>
              <div className="text-[11px] text-slate-400 mt-2 text-center">
                Live preview ·{" "}
                {previewDevice === "mobile"
                  ? "iPhone width (380 px)"
                  : "Desktop width (640 px)"}
              </div>
            </div>

            {preview && (
              <div className="col-span-2 bg-teal-50 border border-teal-100 rounded-xl p-4 text-sm text-teal-800 flex items-start gap-3">
                <Users2 className="w-5 h-5 mt-0.5" />
                <div>
                  <div className="font-medium">
                    Will send to {preview.count} contact(s) with an email
                    address.
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className="text-xs mt-1 text-teal-700">
                      Including:{" "}
                      {preview.sample
                        .map((s) => s.email)
                        .slice(0, 5)
                        .join(", ")}
                      {preview.count > 5 ? "…" : ""}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 bg-stone-50 border border-stone-100 rounded-xl p-4 flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="ns-label">Send a test to yourself</Label>
              <Input
                data-testid={CAMPAIGNS.testEmailInput}
                type="email"
                className="mt-2 h-11 bg-white"
                placeholder="your.email@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            <Button
              data-testid={CAMPAIGNS.sendTestBtn}
              disabled={sendingTest || !testEmail}
              onClick={sendTest}
              variant="outline"
              className="rounded-full h-11 px-5"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingTest ? "Sending…" : "Send test email"}
            </Button>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={CAMPAIGNS.previewBtn}
              variant="outline"
              className="rounded-full"
              onClick={runPreview}
            >
              Preview recipients
            </Button>
            <Button
              data-testid={CAMPAIGNS.sendBtn}
              disabled={sending}
              onClick={send}
              className="rounded-full ns-btn-primary px-6"
            >
              {sending
                ? (form.scheduled_for ? "Scheduling…" : "Sending…")
                : (form.scheduled_for ? "Schedule campaign" : "Send campaign")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog
        open={!!detail}
        onOpenChange={(v) => {
          if (!v) {
            setDetail(null);
            setAnalytics(null);
          }
        }}
      >
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {detail.name}
                </DialogTitle>
              </DialogHeader>
              <div className="ns-label">Subject</div>
              <div className="mb-4">{detail.subject}</div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="ns-card p-4">
                  <div className="ns-label">Recipients</div>
                  <div className="font-serif text-2xl">
                    {detail.total_recipients}
                  </div>
                </div>
                <div className="ns-card p-4">
                  <div className="ns-label">Delivered</div>
                  <div className="font-serif text-2xl text-teal-700">
                    {detail.sent_count}
                  </div>
                </div>
                <div className="ns-card p-4">
                  <div className="ns-label">Failed</div>
                  <div className="font-serif text-2xl text-red-600">
                    {detail.failed_count}
                  </div>
                </div>
              </div>

              {analytics && (
                <div data-testid={CAMPAIGNS.analyticsCard} className="ns-card p-5 mb-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="ns-label">Engagement</div>
                      <div className="font-serif text-xl">Open & click performance</div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="inline-flex items-center gap-1.5 text-teal-700">
                        <Eye className="w-4 h-4" />
                        {analytics.open_rate}% opened
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-indigo-700">
                        <MousePointerClick className="w-4 h-4" />
                        {analytics.click_rate}% clicked
                      </span>
                    </div>
                  </div>

                  <div className="mt-4" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { name: "Delivered", value: analytics.delivered, fill: "#0d7a75" },
                          { name: "Opened", value: analytics.opened, fill: "#0ea5e9" },
                          { name: "Clicked", value: analytics.clicked, fill: "#6366f1" },
                          { name: "Bounced", value: analytics.bounced, fill: "#ef4444" },
                          { name: "Unsubscribed", value: analytics.unsubscribed, fill: "#f59e0b" },
                        ]}
                        margin={{ top: 12, right: 12, left: 0, bottom: 4 }}
                      >
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(13,122,117,0.06)" }} />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                          {[0, 1, 2, 3, 4].map((i) => (
                            <Cell key={i} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 text-[11px] text-slate-400">
                    Open & click tracking updates as Resend webhooks arrive. Bounce rate: {analytics.bounce_rate}%.
                  </div>
                </div>
              )}
              <div className="ns-label mb-2">Body</div>
              <div
                className="email-preview"
                dangerouslySetInnerHTML={{ __html: detail.html_content }}
              />
              <div className="ns-label mt-6 mb-2">Delivery log</div>
              <div className="border border-slate-100 rounded-xl divide-y">
                {detail.sends.map((s, i) => (
                  <div
                    key={i}
                    className="p-3 flex items-center justify-between text-sm"
                  >
                    <div>
                      <div>{s.name || s.email}</div>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </div>
                    <div>
                      {s.status === "sent" ? (
                        <span className="text-teal-700 text-xs">
                          Delivered
                        </span>
                      ) : (
                        <span
                          className="text-red-600 text-xs"
                          title={s.reference}
                        >
                          Failed
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
